import type { UpcomingVisit } from '@/api/types/ProviderApi'
import { visitDayLine, visitTime } from '@/components/VisitDate'
import { ArrivalRow } from '@/components/VisitPage/ArrivalRow'
import { MapWay } from '@/components/VisitPage/MapWay'
import { SitePassRow } from '@/components/VisitPage/SitePassRow'
import { typeLine } from '@/components/VisitPage/StateChip'
import { StateRow } from '@/components/VisitPage/StateRow'
import { VisitSlot } from '@/components/VisitPage/VisitSlot'
import { showsPass, sitePassOf } from '@/features/visits/sitePass'
import { CardGroundProvider } from '@/ui/core/card-ground'
import { Header } from '@/ui/core/header'
import { Text } from '@/ui/core/typography'
import { View } from 'react-native'

/**
 * The visit, as the page: the date as a heading, then the facts about getting to it.
 *
 * The booking confirmation's hero, in black on the pink instead of white on a photograph — the
 * small-caps line, then the day over the hour at fifty-four points, so the two screens about one
 * booking are recognisably the same screen. The photograph stays on Home, where it is what
 * catches an eye on a scrolling page; this page has already been chosen and can lead with the
 * fact.
 *
 * Then three rows on hairlines, with no container: where the studio is, the code that opens the
 * building, and the walk to the door. Rows rather than a card, because a floating white card
 * under a fifty-four-point heading reads as a second page beginning — and rather than a blush
 * group, which is the shape this app gives a *list* of like things, where these are three
 * different answers to "how do I get in".
 *
 * The rows are told they stand on blush so their discs come out white, which is what a mark
 * needs on the pink. The pink is not blush, but the disc question has two answers and this is the
 * one that is right.
 *
 * No state pill. The heading is a heading: `StateRow` says where the visit stands in every state
 * the page can be in, and a capsule at the right margin saying the same thing in two words was a
 * corner mark on a card with no card. The pill still rides Home's photograph, which is an object
 * and has a corner.
 */
export const VisitFacts = ({ visit }: { visit: UpcomingVisit }) => {
	const pass = showsPass(visit) ? sitePassOf(visit) : null
	const now = new Date()

	return (
		<View>
			<Header variant="h7">Upcoming visit</Header>
			{/* The same two lines, set solid, as the confirmation and the card: the day over the
			    hour, which is the order they are asked in.

			    No `adjustsFontSizeToFit`, which the confirmation screen sets: there the block is
			    the last thing in a flex-1 column and has a height to shrink into, and here it is
			    type on a page with none — so iOS shrank it to a sixth of its size to fit a box it
			    had made from the text itself. It does not need it either way. `EEE, MMM d` is
			    eleven characters at most and the alternatives are "Today" and "Tomorrow", so
			    there is no longer string waiting to wrap. */}
			<Header
				variant="h2"
				numberOfLines={2}
				className="mt-3.5 text-[54px] leading-[54px]"
			>
				{`${visitDayLine(visit.startTimeLocal, new Date())}\n${visitTime(
					visit.startTimeLocal
				)}`}
			</Header>
			<Text size="body0" tone="soft" className="mt-3">
				{typeLine(visit)}
			</Text>

			{/* Pulled out by the page's own inset, so each row's fourteen points of padding puts
			    its disc on the column's left edge — under the date rather than indented from it.
			    The negative margin and the rows' own padding cancel, whatever the column is
			    padded by, and the inset rule each row draws for itself lands where its words
			    start.

			    No rule above the first row and none below the last. The type block already ends
			    the heading, so a line between the visit's name and the first row about it divides
			    one thing from itself; and a line above the action turned the button into the last
			    item of the list rather than the thing the page is asking for. What is left are the
			    hairlines between the rows, which are the only ones separating two like things.

			    Twenty-four and a half above, from seventeen and a half. The rows carry fourteen
			    points of their own padding, so the gap reads as smaller than it measures, and the
			    type line above it is the end of a heading rather than a sibling — it wants the
			    air a heading gets, not the air between two rows. */}
			<View className="-mx-4 mt-7">
				<CardGroundProvider ground="blush">
					<MapWay visit={visit} />
					{!!pass && <SitePassRow pass={pass} />}
					<ArrivalRow steps={visit.arrivalSteps ?? []} />
					{/* Last, because it is the only one that is not a door, and because it is the
					    line the button below it follows from. */}
					<StateRow visit={visit} now={now} />
				</CardGroundProvider>
			</View>

			{/* The action on the page, full width, with the line that says why it is dark and the
			    way out of the visit. Inside the card this sits in the rows' own block; here the
			    page column is its inset. */}
			<View className="mt-5">
				<VisitSlot visit={visit} bare />
			</View>
		</View>
	)
}
